import * as React from "react";

import { useNavigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";

import { Modal } from "../../components/modal/Modal";
import { getEmployees, deleteEmployee, enableEmployee, getEmployeesDetails } from "../../redux/actions/employeeActions";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { EmployeeResponse } from "../../api/generated/index";
import { SUCCESS_DELETE, SUCCESS_ENABLED } from "../../constants/messageConstants";
import { handleErrors, handleToast } from "../../utils/helpers";
import { useEffect } from "react";

interface EmployeeTableProps {
  employees: EmployeeResponse[];
}

export const EmployeeTable: React.FC<EmployeeTableProps> = ({ employees }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentItem = React.useRef<EmployeeResponse | null>(null);
  const [showDeletePopup, setShowDeletePopup] = React.useState<boolean>(false);
  const [search, setSearch] = React.useState<string>("");
  const [searchList, setSearchList] = React.useState<EmployeeResponse[]>(employees);

  const { employeeDetails } = useAppSelector((state) => state.employeeDetails);
  const currentItemDetails = React.useMemo(
    () => employeeDetails.find((ed) => ed.email === currentItem.current?.email),
    [employeeDetails, currentItem.current?.email],
  );

  const hasActiveWorksatation = !!currentItemDetails?.office?.length;
  const activeReservations = currentItemDetails?.reservations?.filter((r) => r.isActive) ?? [];

  useEffect(() => {
    dispatch(getEmployeesDetails());
  }, [dispatch]);

  const handleOpen = (item: EmployeeResponse) => {
    setShowDeletePopup(true);
    currentItem.current = item;
  };
  const handleClose = () => {
    setShowDeletePopup(false);
  };

  const onDelete = (id: number) => {
    dispatch(deleteEmployee(id))
      .unwrap()
      .then(() => {
        dispatch(getEmployees());
        dispatch(getEmployeesDetails());
        handleToast(SUCCESS_DELETE);
      })
      .catch((res) => {
        handleErrors(res);
      });
    handleClose();
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    if (value === "") {
      setSearchList(employees);
      return;
    }

    setSearchList(employees.filter((item) => item.name?.toLowerCase().includes(value.toLowerCase().trim())));
  };

  const onCancel = () => {
    handleClose();
  };

  const onAdd = (id: number) => {
    navigate(`new/${id}`);
  };

  const onEnable = (username: string) => {
    dispatch(enableEmployee(username))
      .unwrap()
      .then((res) => {
        if (res) {
          dispatch(getEmployees());
          handleToast(SUCCESS_ENABLED);
        }
      });
  };

  React.useEffect(() => {
    setSearchList(employees);
  }, [employees]);

  return (
    <>
      <Toaster position="top-center" reverseOrder={false} />
      <h2 className="manager__title">
        <span className="manager__title-text">List of added employees</span>

        <input
          className="manager__title-input"
          type="text"
          placeholder="Search by name..."
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
        />
      </h2>
      <div className="manager__content">
        <table className="table">
          <thead>
            <tr className="table__row">
              <th className="table__head">Name</th>
              <th className="table__head">Company</th>
              <th className="table__head">Status</th>
              <th className="table__head">Email</th>
              <td className="table__head">Username</td>
              <th className="table__head">Enabled</th>
              <th className="table__head table__head--action">Actions</th>
            </tr>
          </thead>
          <tbody>
            {searchList.map((item) => {
              return (
                <tr className="table__row" key={item.id}>
                  <td className="table__cell">{item.name}</td>
                  <td className="table__cell">{item.company?.name ? item.company.name : "/"}</td>
                  <td className="table__cell">{item.status ? item.status : "/"}</td>
                  <td className="table__cell">{item.email}</td>
                  <td className="table__cell">{item.username}</td>
                  <td className="table__cell">{item.enabled ? "Yes" : "No"}</td>
                  <td className="table__cell">
                    {!item.enabled ? (
                      <button
                        onClick={() => onEnable(item.username!)}
                        className="link"
                        style={{ width: "100%", margin: "1px" }}>
                        Enable
                      </button>
                    ) : null}
                    <button
                      onClick={() => onAdd(item.id!)}
                      className="link link--icon link--edit"
                      style={{ width: "100%" }}>
                      Edit
                    </button>
                    <button
                      onClick={() => handleOpen(item)}
                      className="link link--icon link--delete"
                      style={{ width: "100%" }}>
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Modal show={showDeletePopup} handleClose={onCancel} size="medium">
        <h2 className="popup__heading-plain">Are you sure you want to delete this employee?</h2>

        {hasActiveWorksatation ? (
          <div>
            <h5>Please remove the employee from the workstation before deleting employee:</h5>
            <p>{`${currentItemDetails.building} -- ${currentItemDetails.floor} -- ${currentItemDetails.office}`}</p>
            <br />
          </div>
        ) : null}
        {activeReservations.length ? (
          <div>
            <h5>Please remove the employee from reservations before deleting employee:</h5>
            {activeReservations.map((r, index) => (
              <p key={`${r.workstationId}-${index}`}>{`${r.building} -- ${r.floor} -- ${r.office}`}</p>
            ))}
            <br />
          </div>
        ) : null}

        <br />

        <div className="popup__actions">
          <button onClick={onCancel} type="button" className="btn btn--transparent popup__btn">
            Cancel
          </button>
          <button
            disabled={hasActiveWorksatation || !!activeReservations.length}
            onClick={() => onDelete(currentItem.current?.id!)}
            type="button"
            className="btn popup__btn">
            Delete
          </button>
        </div>
      </Modal>
    </>
  );
};
